import { FastField, Field, useFormikContext } from 'formik'
import React from 'react'
import { DateTime } from 'luxon'
import {
  CustomCheckboxWithoutInitialValue,
  CustomDateTimePicker,
  CustomRadio,
  CustomRadioGroup,
  CustomTextField,
  FormDatePicker,
} from 'src/form'
import BillingCodes from 'src/participantHistory/intakeNote/BillingCodes'
import { Box, FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material'
import { SubSection } from '../common/Section'
import { CALL_STATUS_OPTIONS, CheckinCallNoteType, shouldShowFullCheckinCallForm } from './CheckinCallNoteSchema'
import { useGetUncancelledInProgramCallsQuery } from './getUncancelledInProgramCalls.generated'

// Sentinel select value for a check-in call that has no dashboard call to link to.
// Never persisted, so it cannot collide with a call ID.
export const CHECK_IN_CALL_OUTSIDE_DASHBOARD = 'OUTSIDE_DASHBOARD'
export const CHECK_IN_CALL_OUTSIDE_DASHBOARD_LABEL = 'Check-in call was done outside of dashboard'

type SelectableCall = {
  id: string
  startTime: Date
  endTime: Date
  checkInCallPatientHistoryEntryID?: string | null
}

type CheckinCallNoteFieldsProps = {
  viewOnly?: boolean
  memberIdLabel?: string
  uid?: string
  selectedCallID?: string | null
  onSelectedCallIDChange?: (callID: string | null) => void
  selectedCallError?: string | null
}

const formatCallOptionLabel = (call: SelectableCall) => {
  const start = DateTime.fromJSDate(call.startTime)
  const baseLabel = `${start.toFormat('MMM d, yyyy')} · ${start.toFormat('h:mm a')}`
  return call.checkInCallPatientHistoryEntryID ? `${baseLabel} (note linked)` : baseLabel
}

export function CheckinCallNoteFields({
  viewOnly,
  memberIdLabel = 'Member ID',
  uid,
  selectedCallID,
  onSelectedCallIDChange,
  selectedCallError,
}: CheckinCallNoteFieldsProps) {
  const { values, setFieldValue } = useFormikContext<CheckinCallNoteType>()
  const { data, loading } = useGetUncancelledInProgramCallsQuery({
    variables: { uid: uid ?? '' },
    skip: viewOnly || !uid,
  })

  const selectableCalls = (data?.patient?.inProgramVideoCallBookingInformation?.uncancelledCalls ??
    []) as SelectableCall[]
  const selectedCallInList =
    !selectedCallID ||
    selectedCallID === CHECK_IN_CALL_OUTSIDE_DASHBOARD ||
    selectableCalls.some((call) => call.id === selectedCallID)

  const handleCallChange = (event: SelectChangeEvent<string>) => {
    const nextCallID = event.target.value || null
    onSelectedCallIDChange?.(nextCallID)

    if (!nextCallID || nextCallID === CHECK_IN_CALL_OUTSIDE_DASHBOARD) {
      return
    }

    const selectedCall = selectableCalls.find((call) => call.id === nextCallID)
    if (!selectedCall) {
      return
    }

    setFieldValue('date', selectedCall.startTime.toISOString())
    setFieldValue('CALL_START_TIME', selectedCall.startTime.toISOString())
    setFieldValue('CALL_END_TIME', '')
  }

  return (
    <>
      <SubSection>
        <Grid container columnSpacing={2.5} rowSpacing={0}>
          {!viewOnly && uid && (
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small" disabled={loading} error={Boolean(selectedCallError)}>
                <InputLabel id="check-in-call-select-label" shrink>
                  Linked check-in call
                </InputLabel>
                <Select
                  labelId="check-in-call-select-label"
                  label="Linked check-in call"
                  notched
                  fullWidth
                  value={selectedCallID ?? ''}
                  onChange={handleCallChange}
                  displayEmpty
                  renderValue={(value) => {
                    if (!value) {
                      return 'None'
                    }
                    if (value === CHECK_IN_CALL_OUTSIDE_DASHBOARD) {
                      return CHECK_IN_CALL_OUTSIDE_DASHBOARD_LABEL
                    }
                    const selectedCall = selectableCalls.find((call) => call.id === value)
                    if (selectedCall) {
                      return formatCallOptionLabel(selectedCall)
                    }
                    return 'Selected call'
                  }}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value={CHECK_IN_CALL_OUTSIDE_DASHBOARD}>{CHECK_IN_CALL_OUTSIDE_DASHBOARD_LABEL}</MenuItem>
                  {selectedCallID && !selectedCallInList && <MenuItem value={selectedCallID}>Selected call</MenuItem>}
                  {selectableCalls.map((call) => (
                    <MenuItem key={call.id} value={call.id}>
                      {formatCallOptionLabel(call)}
                    </MenuItem>
                  ))}
                </Select>
                {selectedCallError && <FormHelperText>{selectedCallError}</FormHelperText>}
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} md={4}>
            <FastField component={CustomTextField} fullWidth label="First name" name="FIRST_NAME" />
          </Grid>
          <Grid item xs={12} md={4}>
            <FastField component={CustomTextField} fullWidth label="Last name" name="LAST_NAME" />
          </Grid>
          <Grid item xs={12} md={4}>
            <FastField component={CustomTextField} fullWidth label={memberIdLabel} name="MEMBER_ID" />
          </Grid>
          <Grid item xs={12} md={4}>
            <FastField component={FormDatePicker} fullWidth label="Date of event" name="date" />
          </Grid>
          <Grid item xs={12} md={4}>
            <Field
              component={CustomDateTimePicker}
              fullWidth
              timeOnly
              label="Call start time"
              name="CALL_START_TIME"
              referenceDateString={values.date}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Field
              component={CustomDateTimePicker}
              fullWidth
              timeOnly
              label="Call end time"
              name="CALL_END_TIME"
              referenceDateString={values.date}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FastField component={CustomTextField} fullWidth label="Provider name" name="PROVIDER_NAME" />
          </Grid>
          <Grid item xs={12} md={4}>
            <FastField component={CustomTextField} fullWidth label="License type" name="LICENSE_TYPE" />
          </Grid>
          <Grid item xs={12} md={4}>
            <FastField component={CustomTextField} fullWidth label="Call status" name="CALL_STATUS" type="select">
              {CALL_STATUS_OPTIONS.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </FastField>
          </Grid>
        </Grid>
      </SubSection>

      {shouldShowFullCheckinCallForm(values.CALL_STATUS) ? (
        <>
          <SubSection title="Service Delivery & Compliance">
            <Box mt={2}>
              <FastField
                fullWidth
                component={CustomCheckboxWithoutInitialValue}
                name="SERVICE_DELIVERY_AND_COMPLIANCE.CONFIRM_SERVICE_PROVIDED_VIA_TELEHEALTH"
                label="Service provided via telehealth"
              />
              <FastField
                fullWidth
                component={CustomCheckboxWithoutInitialValue}
                name="SERVICE_DELIVERY_AND_COMPLIANCE.CONFIRM_SERVICE_PROVIDED_VIA_HIPAA_COMPLIANT_SOFTWARE"
                label="Service provided via HIPAA compliant software"
              />
              <FastField
                fullWidth
                component={CustomCheckboxWithoutInitialValue}
                name="SERVICE_DELIVERY_AND_COMPLIANCE.CONFIRM_CALL_TIME_AND_LOCATION"
                label="Participant confirmed location at time of call"
              />
            </Box>
          </SubSection>

          <SubSection title="Call Method">
            <FastField component={CustomRadioGroup} name="CALL_METHOD" fullWidth>
              <CustomRadio label="In-app" value="IN_APP" />
              <CustomRadio label="Phone call" value="PHONE_CALL" />
            </FastField>
          </SubSection>

          <SubSection title="SMART Goals">
            <FastField
              component={CustomTextField}
              label="SMART Goals"
              fullWidth
              multiline
              name="SMART_GOALS"
              minRows={2}
            />
          </SubSection>

          <SubSection title="Billing and Diagnosis Codes">
            <BillingCodes name="BILLING_CODES" viewOnly={viewOnly} />
          </SubSection>

          <SubSection title="Reason for Call">
            <FastField
              component={CustomTextField}
              fullWidth
              label="Reason for Call"
              name="REASON_FOR_CALL"
              type="select"
            >
              {REASON_FOR_CALL_SELECTIONS.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </FastField>
          </SubSection>

          <SubSection title="Interventions">
            <Grid item xs={12}>
              {INTERVENTIONS.map((intervention) => (
                <FastField
                  key={`INTERVENTIONS_AND_PARTICIPANT_AGREEMENT.${intervention.name}`}
                  fullWidth
                  component={CustomCheckboxWithoutInitialValue}
                  name={`INTERVENTIONS.${intervention.name}`}
                  label={intervention.label}
                />
              ))}
              <FastField
                component={CustomTextField}
                fullWidth
                multiline
                label="Brief summary of call"
                name="INTERVENTIONS.OTHER"
                minRows={2}
              />
            </Grid>
          </SubSection>

          <SubSection title="Risk Assessment">
            <Field component={CustomRadioGroup} name="RISK_LEVEL_HAS_CHANGE">
              <CustomRadio label="No known changes to risk level since last encounter" value="no" />
              <CustomRadio label="Changes to risk level" value="yes" />
            </Field>
          </SubSection>

          {values.RISK_LEVEL_HAS_CHANGE === 'yes' && (
            <>
              <SubSection title="Protective Factors Identified">
                <Grid item xs={12}>
                  {PROTECTIVE_FACTORS_IDENTIFIED.map((protectiveFactor) => (
                    <FastField
                      key={`PROTECTIVE_FACTORS_IDENTIFIED.${protectiveFactor.name}`}
                      fullWidth
                      component={CustomCheckboxWithoutInitialValue}
                      name={`PROTECTIVE_FACTORS_IDENTIFIED.${protectiveFactor.name}`}
                      label={protectiveFactor.label}
                    />
                  ))}
                  <FastField
                    component={CustomTextField}
                    fullWidth
                    multiline
                    label="Other (if applicable)"
                    name="PROTECTIVE_FACTORS_IDENTIFIED.OTHER"
                    minRows={2}
                  />
                </Grid>
              </SubSection>

              <SubSection title="Next Steps">
                <Grid item xs={12}>
                  {NEXT_STEPS.map((nextStep) => (
                    <FastField
                      key={`NEXT_STEPS.${nextStep.name}`}
                      fullWidth
                      component={CustomCheckboxWithoutInitialValue}
                      name={`NEXT_STEPS.${nextStep.name}`}
                      label={nextStep.label}
                    />
                  ))}
                  <FastField
                    component={CustomTextField}
                    fullWidth
                    multiline
                    label="Other (if applicable)"
                    name="NEXT_STEPS.OTHER"
                    minRows={2}
                  />
                </Grid>
              </SubSection>
            </>
          )}

          <SubSection title="Participant Response">
            <Grid item xs={12}>
              {PARTICIPANT_RESPONSE.map((participantResponse) => (
                <FastField
                  key={`PARTICIPANT_RESPONSE.${participantResponse.name}`}
                  fullWidth
                  component={CustomCheckboxWithoutInitialValue}
                  name={`PARTICIPANT_RESPONSE.${participantResponse.name}`}
                  label={participantResponse.label}
                />
              ))}
              <FastField
                component={CustomTextField}
                fullWidth
                multiline
                label="Other (if applicable)"
                name="PARTICIPANT_RESPONSE.OTHER"
                minRows={2}
              />
            </Grid>
          </SubSection>

          <SubSection title="Plan">
            <Grid item xs={12}>
              {PLANS.map((plan) => (
                <FastField
                  key={`PLAN.${plan.name}`}
                  fullWidth
                  component={CustomCheckboxWithoutInitialValue}
                  name={`PLAN.${plan.name}`}
                  label={plan.label}
                />
              ))}
              <FastField
                component={CustomTextField}
                fullWidth
                multiline
                label="Other (if applicable)"
                name="PLAN.OTHER"
                minRows={2}
              />
            </Grid>
          </SubSection>
        </>
      ) : (
        <SubSection title="Description">
          <FastField
            component={CustomTextField}
            fullWidth
            multiline
            label="Description"
            name="DESCRIPTION"
            minRows={4}
          />
        </SubSection>
      )}
    </>
  )
}

const REASON_FOR_CALL_SELECTIONS = [
  { label: 'Program engagement', value: 'PROGRAM_ENGAGEMENT' },
  { label: 'New stressor or life event', value: 'NEW_STRESSOR_OR_LIFE_EVENT' },
  { label: 'Symptom management and/or escalation', value: 'SYMPTOM_MANAGEMENT_AND_OR_ESCALATION' },
  { label: 'Crisis management and/or safety planning', value: 'CRISIS_MANAGEMENT_AND_OR_SAFETY_PLANNING' },
  { label: 'Support system issues', value: 'SUPPORT_SYSTEM_ISSUE' },
  { label: 'Therapeutic alliance/relationship', value: 'THERAPEUTIC_ALLIANCE_OR_RELATIONSHIP' },
  { label: 'End of program wrap-up', value: 'END_OF_PROGRAM_WRAP_UP' },
]

const INTERVENTIONS = [
  { label: 'Cognitive Behavioral Therapy (CBT) techniques', name: 'COGNITIVE_BEHAVIORAL_THERAPY_TECHNIQUES' },
  { label: 'Cognitive restructuring', name: 'COGNITIVE_RESTRUCTURING' },
  { label: 'Coping skills development', name: 'COPING_SKILLS_DEVELOPMENT' },
  { label: 'Relapse prevention planning', name: 'RELAPSE_PREVENTION_PLANNING' },
  { label: 'Mindfulness techniques', name: 'MINDFULNESS_TECHNIQUES' },
  { label: 'Communication skills training', name: 'COMMUNICATION_SKILLS_TRAINING' },
  { label: 'Solution-Focused Brief Therapy (SFBT) techniques', name: 'SOLUTION_FOCUSED_BRIEF_THERAPY_TECHNIQUES' },
  { label: 'Crisis intervention (if applicable)', name: 'CRISIS_INTERVENTION' },
  { label: 'Motivational interviewing (MI) techniques', name: 'MOTIVATIONAL_INTERVIEWING_TECHNIQUES' },
  { label: 'Resource linkage', name: 'RESOURCE_LINKAGE' },
]

const PROTECTIVE_FACTORS_IDENTIFIED = [
  { label: 'Strong social support', name: 'STRONG_SOCIAL_SUPPORT' },
  { label: 'Engagement in therapy', name: 'ENGAGEMENT_IN_THERAPY' },
  { label: 'Coping skills', name: 'COPING_SKILLS' },
  { label: 'Future-oriented thinking', name: 'FUTURE_ORIENTED_THINKING' },
  { label: 'Motivated for treatment', name: 'MOTIVATED_FOR_TREATMENT' },
  { label: 'Religious/Spiritual beliefs', name: 'RELIGIOUS_BELIEFS' },
]

const NEXT_STEPS = [
  { label: 'Safety plan developed, reviewed and emailed to client', name: 'SAFETY_PLAN_REVIEWED_EMAILED' },
  { label: 'Safety plan documented in medical record', name: 'SAFETY_PLAN_DOCUMENTED_IN_RECORD' },
  { label: 'Safety plan not required due to passive SI/low risk', name: 'NO_FURTHER_ASSISTANCE_NEEDED' },
  {
    label: 'Law enforcement/emergency contact notified due to imminent risk',
    name: 'LAW_ENFORCEMENT_OR_EMERGENCY_CONTACT_NOTIFIED',
  },
]

const PARTICIPANT_RESPONSE = [
  {
    label: 'Engaged in discussion and showed understanding of techniques',
    name: 'ENGAGED_AND_SHOWED_UNDERSTANDING_OF_TECHNIQUES',
  },
  { label: 'Took an active role in problem-solving and generating solutions', name: 'ACTIVE_IN_PROBLEM_SOLVING' },
  { label: 'Highlighted personal strengths and resources', name: 'HIGHLIGHTED_PERSONAL_STRENGTH_AND_RESOURCES' },
  { label: 'Practiced and built confidence with new skills', name: 'PRACTICED_AND_BUILT_CONFIDENCE' },
  {
    label: 'Expressed motivation to apply strategies beyond the session',
    name: 'EXPRESSED_MOTIVATION_TO_APPLY_TECHNIQUES',
  },
]

const PLANS = [
  {
    label: 'Continue to offer support and personalized treatment via in-app chat',
    name: 'CONTINUE_SUPPORT_VIA_APP_CHAT',
  },
  { label: 'Offer check-in calls as clinically appropriate', name: 'OFFER_CHECK_IN_CALLS_AS_NEEDED' },
  {
    label: 'Disenroll participant at their request or due to engagement issues',
    name: 'DISENROLL_PARTICIPANT_AT_REQUEST',
  },
  { label: 'Send resources as discussed during the call', name: 'SEND_RESOURCES_AND_INFORMATION' },
]
